import { EmeraldIcon } from "@emerald-react/icon";
import type { SurveyPOICategory } from "@survey-types/survey.poi.types";
import {
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface CategoryOption {
  value: string;
  label: string;
}

interface CategorySelectWithRenameProps {
  label: string;
  placeholder?: string;
  value: string;
  options: CategoryOption[];
  /** Full category objects — needed to pass the selected category to the rename modal */
  categories: SurveyPOICategory[];
  onChange: (value: string) => void;
  onFocus?: () => void;
  /** Called with the category extId when the pencil icon is clicked */
  onEditCategory: (categoryId: string) => void;
}

const CREATE_NEW_VALUE = "__CREATE_NEW__";
const LISTBOX_ID = "category-select-listbox";

/**
 * A custom category select that renders a pencil (edit) icon next to the
 * currently selected category when the dropdown is open.
 *
 * Behaviour:
 *  - Closed, no value   → shows only the floating label centred (acts as placeholder).
 *  - Closed, has value  → label floats to top-left; selected value shown below.
 *  - Open               → label floats to top-left; search <input> in trigger area;
 *                         options list drops below (selected item first + pencil icon).
 */
const CategorySelectWithRename = ({
  label,
  placeholder = "Search or create a category...",
  value,
  options,
  categories,
  onChange,
  onFocus,
  onEditCategory,
}: CategorySelectWithRenameProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedOption = useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen) {
      const id = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [isOpen]);

  const openDropdown = () => {
    if (!isOpen) {
      onFocus?.();
      setIsOpen(true);
      setSearchQuery("");
    }
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleTriggerClick = () => {
    if (isOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  };

  const handleOptionSelect = (optionValue: string) => {
    onChange(optionValue);
    closeDropdown();
  };

  const handlePencilClick = useCallback(
    (e: ReactMouseEvent, categoryId: string) => {
      e.stopPropagation();
      closeDropdown();
      onEditCategory(categoryId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onEditCategory],
  );

  const handleContainerKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") closeDropdown();
    if ((e.key === "Enter" || e.key === " ") && !isOpen) {
      e.preventDefault();
      openDropdown();
    }
  };

  const handleOptionKeyDown = (
    e: KeyboardEvent<HTMLLIElement>,
    optionValue: string,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleOptionSelect(optionValue);
    }
  };

  // Label floats when open or a value is selected
  const isFloating = isOpen || !!selectedOption;

  // Options list: selected item first, then others (filtered by search), then "+ Create New"
  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const regular = options.filter((o) => o.value !== CREATE_NEW_VALUE);
    const createNew = options.find((o) => o.value === CREATE_NEW_VALUE);

    const selected = regular.filter((o) => o.value === value);
    const others = regular.filter(
      (o) =>
        o.value !== value &&
        (query === "" || o.label.toLowerCase().includes(query)),
    );
    const selectedVisible =
      query === "" || selected.some((o) => o.label.toLowerCase().includes(query))
        ? selected
        : [];

    const result: Array<CategoryOption & { isSelected?: boolean }> = [
      ...selectedVisible.map((o) => ({ ...o, isSelected: true })),
      ...others,
    ];
    if (createNew) result.push(createNew);
    return result;
  }, [options, value, searchQuery]);

  const getOptionClassName = (
    isCreateNew: boolean,
    isCurrentlySelected: boolean | undefined,
  ): string => {
    if (isCreateNew) return "bg-white text-primary hover:bg-slate-50";
    if (isCurrentlySelected)
      return "bg-[#003f2d1a] text-[#003f2d] hover:bg-[#003f2d26]";
    return "bg-white text-slate-800 hover:bg-slate-50";
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      onKeyDown={handleContainerKeyDown}
      tabIndex={0}
    >
      {/* ── Trigger field ─────────────────────────────────────────────── */}
      <div
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={LISTBOX_ID}
        className={`relative flex min-h-[42px] w-full cursor-pointer items-center rounded border bg-white px-3 py-1 text-sm transition-colors ${
          isOpen
            ? "border-primary shadow-sm"
            : "border-slate-300 hover:border-slate-400"
        }`}
        onClick={handleTriggerClick}
      >
        {/* Floating label */}
        <span
          className={`pointer-events-none absolute left-3 transition-all duration-150 ${
            isFloating
              ? "top-[5px] text-[10px] font-medium text-primary"
              : "top-1/2 -translate-y-1/2 text-sm text-slate-500"
          }`}
        >
          {label}
        </span>

        {/* Content area — search input when open, selected value when closed */}
        <div className="flex flex-1 items-center pt-3">
          {isOpen ? (
            /* Search input lives inside the trigger when open */
            <input
              ref={searchInputRef}
              type="text"
              className="w-full bg-transparent text-sm text-slate-800 placeholder-slate-400 outline-none"
              placeholder={placeholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()} // prevent trigger toggle on input click
              aria-label="Search categories"
            />
          ) : selectedOption ? (
            <span className="truncate text-slate-800">{selectedOption.label}</span>
          ) : null /* closed + no value: label alone acts as placeholder */}
        </div>

        {/* Chevron */}
        <EmeraldIcon
          icon={isOpen ? "arrow_drop_up" : "arrow_drop_down"}
          variant="icon"
          fontsizeInRem={1.25}
          className="ml-1 shrink-0 text-slate-500"
        />
      </div>

      {/* ── Dropdown options list ──────────────────────────────────────── */}
      {isOpen && (
        <ul
          id={LISTBOX_ID}
          role="listbox"
          aria-label="Category options"
          className="absolute left-0 right-0 z-[1300] mt-1 max-h-56 overflow-y-auto rounded border border-slate-200 bg-white shadow-lg"
          style={{ scrollbarWidth: "thin" }}
        >
          {filteredOptions.length === 0 && (
            <li
              className="px-4 py-2 text-sm text-slate-400"
              role="option"
              aria-selected={false}
            >
              No categories found
            </li>
          )}
          {filteredOptions.map((option) => {
            const isCreateNew = option.value === CREATE_NEW_VALUE;
            const isCurrentlySelected = option.isSelected;
            const optionClassName = getOptionClassName(isCreateNew, isCurrentlySelected);

            return (
              <li
                key={option.value}
                role="option"
                tabIndex={0}
                aria-selected={isCurrentlySelected ?? false}
                className={`flex cursor-pointer items-center px-4 py-2 text-sm transition-colors ${optionClassName}`}
                onClick={() => handleOptionSelect(option.value)}
                onKeyDown={(e) => handleOptionKeyDown(e, option.value)}
              >
                <span className="flex-1 truncate">{option.label}</span>

                {/* Pencil icon — selected non-special option only */}
                {isCurrentlySelected && !isCreateNew && (
                  <button
                    type="button"
                    aria-label={`Rename category ${option.label}`}
                    className="ml-2 shrink-0 rounded p-0.5 text-slate-400 transition-colors hover:bg-white hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    onClick={(e) => {
                      const matchedCat = categories.find(
                        (c) => c.categoryName === option.label,
                      );
                      handlePencilClick(e, matchedCat?.extId ?? option.value);
                    }}
                  >
                    <EmeraldIcon icon="edit" variant="icon" fontsizeInRem={1} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CategorySelectWithRename;

